import { useQueryClient } from "@tanstack/react-query";
import { FC, useCallback, useEffect } from "react";
import { FormattedMessage } from "react-intl";
import { useNavigate } from "react-router-dom";
import {
  getListOrganizationsQueryKey,
  useListOrganizations,
} from "~/api/organizations/organizations";
import { useGetCurrentUser } from "~/api/users/users";
import { Loader } from "~/components/Loader";
import { PageLayout } from "~/components/PageLayout";
import { SKY_SELECTED_ORGANIZATION_ID } from "~/constants";
import { ROUTES } from "~/helpers";

const RootContent: FC = () => {
  const navigate = useNavigate();
  const { data, error } = useListOrganizations();
  const { data: currentUser } = useGetCurrentUser();
  const queryClient = useQueryClient();

  const navigateToOrg = useCallback(
    (organizationId: string) => {
      localStorage.setItem(SKY_SELECTED_ORGANIZATION_ID, organizationId);
      navigate(ROUTES.CASES({ organizationId }));
    },
    [navigate]
  );

  useEffect(() => {
    if (error) {
      if ((error as any).code === 401) {
        queryClient.invalidateQueries({
          queryKey: getListOrganizationsQueryKey(),
        });
      }
      return;
    }

    if (!data || !currentUser) return;

    const homeOrgs = data.filter(
      (org) => !currentUser.guestInOrganizations?.includes(org.id!)
    );

    // If user already selected an org this session, go straight there
    const savedOrgId = localStorage.getItem(SKY_SELECTED_ORGANIZATION_ID);
    if (savedOrgId && homeOrgs.some((org) => org.id === savedOrgId)) {
      navigateToOrg(savedOrgId);
      return;
    }

    // If more than one home org, let the user choose
    if (homeOrgs.length == 0) {
      navigate(ROUTES.SELECT_ORG);
      return;
    }
    if (homeOrgs.length > 1) {
      navigate(ROUTES.SELECT_ORG);
      return;
    }

    const targetOrg = homeOrgs[0] || data[0];
    const organizationId = targetOrg?.id;

    if (
      organizationId &&
      currentUser.guestInOrganizations?.includes(organizationId)
    ) {
      navigate(ROUTES.ACCESS_DENIED);
      return;
    }

    if (organizationId) {
      navigateToOrg(organizationId);
    }
  }, [data, error, navigate, queryClient, currentUser, navigateToOrg]);

  // if (data && data.length > 0) {
  //Hide demo case for all users
  // if (
  //   localStorage.getItem(SKY_ACCOUNT_CREATED) === "true" &&
  //   localStorage.getItem(SKY_LAST_TRANSCRIBED_TEXT)
  // ) {
  //   navigate(ROUTES.DEMO_CASE_GENERATION({ organizationId: data[0].id }));
  // } else {
  //   navigate(ROUTES.CASES({ organizationId: data[0].id }));
  // }
  // }

  return (
    <PageLayout>
      <div className="flex flex-col items-center gap-2 h-[70vh] justify-center">
        <Loader>
          <div className="text-center mt-4">
            <FormattedMessage
              id="noOrganizations"
              defaultMessage="Please wait. If loading persists, <support>contact support</support>."
              values={{
                support: (chunks) => (
                  <a
                    href="mailto:support@sky-tech.ai"
                    className="text-blue-500 hover:underline"
                  >
                    {chunks}
                  </a>
                ),
              }}
            />
          </div>
        </Loader>
      </div>
    </PageLayout>
  );
};

export const RootPage: FC = () => {
  return <RootContent />;
};
